import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { GenerationResultCard } from './GenerationResultCard';
import { BatchGenerationCard } from './BatchGenerationCard';
import type { GenerationResult } from '@/utils/visualGenerationHandlers';

interface GenerationResultsGridProps {
  results: GenerationResult[];
  onPreview?: (imageUrl: string) => void;
  onDownload?: (imageUrl: string, filename: string) => void;
  onUseAsInput?: (imageUrl: string) => void;
  onSetAsPreview?: (result: GenerationResult) => void;
  onDelete?: (result: GenerationResult) => void;
  onDeleteBatch?: (result: GenerationResult) => void;
  className?: string;
}

export const GenerationResultsGrid: React.FC<GenerationResultsGridProps> = ({
  results,
  onPreview,
  onDownload,
  onUseAsInput,
  onSetAsPreview,
  onDelete,
  onDeleteBatch,
  className
}) => {
  if (results.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="p-8 text-center">
          <div className="text-muted-foreground">
            No generations yet. Start by selecting a purpose and generating your first visual.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {results.map((result, index) => {
            // Check if it's a batch generation (single result with multiple images)
            if (result.imageUrls && result.imageUrls.length > 1 && result.generationType === 'batch') {
              return (
                <BatchGenerationCard
                  key={`${result.id}-${index}`}
                  result={result}
                  onPreview={onPreview}
                  onDownload={onDownload}
                  onUseAsInput={onUseAsInput}
                  onSetAsPreview={onSetAsPreview}
                  onDelete={onDeleteBatch}
                />
              );
            }
            
            // Render single generation
            return (
              <GenerationResultCard
                key={`${result.id}-${index}`}
                result={result}
                onPreview={onPreview}
                onDownload={onDownload}
                onUseAsInput={onUseAsInput}
                onSetAsPreview={onSetAsPreview}
                onDelete={onDelete}
              />
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};